import { yupResolver } from '@hookform/resolvers/yup';
import { isSupported } from 'firebase/messaging';
import * as yup from 'yup';

import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import Button from 'components/field/Button';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Typography from 'components/global/Typography';

import useAlert from 'hooks/useAlert';
import { appTrackingData } from 'hooks/useAppTracking';
import useFirebaseSubscriber from 'hooks/useFirebaseSubscriber';
import useLocale from 'hooks/useLocale';
import useRefreshToken from 'hooks/useRefreshToken';

import axios from 'services/axios';
import { REGISTER } from 'services/consts';
import { deleteFCMToken } from 'services/firebase';

import AppLogger from 'utils/AppLogger';
import FirebaseAuthService from 'utils/FirebaseAuthService';
import PATHS from 'utils/paths';

import * as S from './Common.styled';
import RegisterSecondStep from './RegisterSecondStep';

const RegisterWithProvider = () => {
	const { showError } = useAlert();
	const { state } = useLocation();
	const [loading, setLoading] = useState(false);
	const navigate = useNavigate();
	const { t } = useTranslation();
	const [searchParams] = useSearchParams();
	const referralToken = searchParams?.get('referralToken');
	const otoProduct = searchParams?.get('product');
	const { LangCountryCode } = useLocale();
	const refreshAccess = useRefreshToken();

	const user = useFirebaseSubscriber();

	const schema = useMemo(
		() =>
			yup.object().shape({
				companyName: yup
					.string()
					.nullable()
					.when(['haveCompanyName'], {
						is: (haveCompanyName) => haveCompanyName === true,
						then: (schema) => schema.required()
					}),
				firstName: yup.string().nullable().required(),
				lastName: yup.string().nullable().required(),
				phone: yup.string().nullable().required().phoneNumber(),
				countryID: yup.number().nullable().required(),
				promoCode: yup.string().nullable()
			}),
		[t]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			companyName: null,
			phone: null,
			countryID: null,
			promoCode: null,
			firstName: null,
			lastName: null
		}
	});

	const { getValues, handleSubmit, reset, setError } = formMethods;

	useEffect(() => {
		if (user?.displayName) {
			const names = user?.displayName?.split(' ');
			const firstName = names?.[0];
			const lastName = names?.slice(1)?.join(' ');

			reset({
				...getValues(),
				firstName,
				lastName
			});
		}
	}, [user]);

	const onSubmit = async (data) => {
		if (!user?.uid) {
			showError({
				message: 'error.G001'
			});

			navigate({ pathname: `/login/${LangCountryCode}`, search: searchParams.toString() });
			return;
		}

		setLoading(true);

		await axios
			.post(REGISTER.REGISTER_OAUTH, {
				...data,
				UID: user.uid,
				locale: LangCountryCode,
				aggreedTerms: true,
				referralToken,
				otoProduct
			})
			.then(async (response) => {
				const { status } = response;
				if (status === 200) {
					appTrackingData.isNewRegistered = true;

					try {
						await isSupported().then(async (isSupported) => {
							isSupported && (await deleteFCMToken());
						});
					} catch {
						// do nothing
					}

					await refreshAccess();
					await FirebaseAuthService.logoutUser();

					if (state?.redirectTo) {
						navigate(state.redirectTo.pathname + state.redirectTo.search);
					} else {
						navigate(PATHS.BASE);
					}
				} else {
					AppLogger.error(response);
					showError();
				}
			})
			.catch((error) => {
				if (error?.response?.data?.errors) {
					error.response.data.errors.forEach((err) => {
						const errorCode = err?.code;

						const ErrorFields = {
							'error.G002': 'companyName',
							'error.G003': 'mail',
							'error.G008': 'phone'
						};

						const fieldName = ErrorFields[errorCode];
						if (fieldName) {
							setError(fieldName, { type: 'custom', message: t(errorCode) }, { shouldFocus: true });
						} else {
							AppLogger.error(error);
							showError({
								message: errorCode
							});
						}
					});
				} else {
					AppLogger.error(error);
					showError({
						message: error?.response?.data?.code
					});
				}
			});

		setLoading(false);
	};

	return (
		<Form
			formMethods={formMethods}
			borderless
			style={{
				display: 'flex',
				justifyContent: 'center'
			}}>
			<S.Container>
				<S.Content>
					<div>
						<RegisterSecondStep />
					</div>

					<FormItem>
						<Button
							size="large"
							loading={loading}
							style={{ width: '100%' }}
							loadingLabel="signingUp"
							onClick={handleSubmit(onSubmit, (errors) => {
								AppLogger.log(errors);
							})}>
							<Typography className="button-text" fontWeight="500">
								signUp
							</Typography>
						</Button>
					</FormItem>
				</S.Content>
			</S.Container>
		</Form>
	);
};

export default RegisterWithProvider;
